const humps = require('../utils/humps');
const legacy = require('./legacy-support');

function AppBridge () {
  this.version = require('./version');
  this.util = INKIVE.Helpers;

  _.bindAll(this, 'propagate');
}

AppBridge.prototype = _.extend({}, Backbone.Events);

AppBridge.prototype.initialize = function () {
  legacy.supportsDesignFacade(this) && initDesignFacade.call(this);
  legacy.supportsEditorFacade(this) && initEditorFacade.call(this);
  legacy.supportsProjectFacade(this) && initProjectFacade.call(this);
  legacy.supportsRestrictionsFacade(this) && initPermissionsFacade.call(this);
  initAnimationFacade.call(this);

  initFactories.call(this);
  initSupportList.call(this);

  this.getFontUrl = INKIVE.FontsFactory.getFontUrl;

  legacy.events(this);

  function initFactories () {
    const propagate = this.propagate;
    const shapesFactory = require('../factory/shapes');

    this.factories = {
      shapes: {
        get: function () {
          shapesFactory.get.apply(shapesFactory, arguments).then(_.partial(
            propagate, 'factory', 'shapes_data'
          ));
        },
        getCategories: function () {
          shapesFactory.getCategories().then(_.partial(
            propagate, 'factory', 'shapes_categories'
          ));
        }
      }
    };
  }

  function initProjectFacade () {
    const ProjectFacade = require('../facade/project/project');
    ProjectFacade.on('all', _.partial(this.propagate, 'project'));
    this.project = {
      call: ProjectFacade.handle,
      get: function () { return ProjectFacade.get.apply(ProjectFacade, arguments); }
    };
  }

  function initDesignFacade () {
    const DesignFacade = require('../facade/design/design');
    const supportsCreateReturn = legacy.supportsCreateReturn(this);
    DesignFacade.on('all', _.partial(this.propagate, 'design'));

    this.design = {
      set: DesignFacade.set,
      create: function () {
        const result = DesignFacade.create.apply(DesignFacade, arguments);
        if (supportsCreateReturn) return result;
      },
      call: function () {
        const result = DesignFacade.handle.apply(DesignFacade, arguments);
        if (!window.Android) return JSON.stringify(result);
        appBridge.propagate('design', 'call', result);
      },
      get: function (id, path) {
        const result = DesignFacade.get.apply(DesignFacade, arguments);
        if (appBridge.version.isApp('webrand') || appBridge.version.isApp('pdf') ||
          !window.Android || !appBridge.version.isLowerThan('2.2.6')) {
          return window.Android ? result : JSON.stringify(result);
        }

        let eventKey = 'get';

        if (!appBridge.version.isLowerThan('1.1.3')) {
          eventKey += '.' + id;
          eventKey += path ? ('.' + path) : '';
        }

        appBridge.propagate('design', eventKey, result);
      }
    };
  }

  function initPermissionsFacade () {
    const RestrictionsFacade = require('../facade/restrictions/restrictions');

    this.permissions = {
      getDefaults: function () {
        return (window.Android && (appBridge.version.isApp('webrand') || appBridge.version.isApp('pdf') ||
          !appBridge.version.isLowerThan('2.2.6')))
          ? RestrictionsFacade.getAllDefaultPermissions()
          : JSON.stringify(RestrictionsFacade.getAllDefaultPermissions());
      },
      getByElement: function (id) {
        return (window.Android && (appBridge.version.isApp('webrand') || appBridge.version.isApp('pdf') ||
          !appBridge.version.isLowerThan('2.2.6')))
          ? RestrictionsFacade.getAllElementPermissions(id)
          : JSON.stringify(RestrictionsFacade.getAllElementPermissions(id));
      }
    };
  }

  function initEditorFacade () {
    const Advice = require('../utils/advice');
    const EditorFacade = require('../facade/editor/editor');
    const ImagesFactory = require('../factory/images');
    Advice.on('all', _.partial(this.propagate, 'editor.advice'));
    EditorFacade.on('all', _.partial(this.propagate, 'editor'));

    this.editor = {
      setImagesData: _.bind(ImagesFactory.setImagesData, ImagesFactory),
      call: function () {
        const result = EditorFacade.handle.apply(EditorFacade, arguments);
        if (!window.Android) return JSON.stringify(result);
        appBridge.propagate('editor', 'call', result);
      },
      get: function (handler, path) {
        const result = EditorFacade.get.apply(EditorFacade, arguments);
        if (appBridge.version.isApp('webrand') || appBridge.version.isApp('pdf') ||
          !window.Android || !appBridge.version.isLowerThan('2.2.6')) {
          return window.Android ? result : JSON.stringify(result);
        }

        let eventKey = 'get';

        if (appBridge.version.isApp('webrand') || appBridge.version.isApp('pdf') ||
          !appBridge.version.isLowerThan('1.1.3')) {
          eventKey += '.' + handler;
          eventKey += path ? ('.' + path) : '';
        }

        appBridge.propagate('editor', eventKey, result);
      }
    };
  }

  function initSupportList () {
    const support = require('./support.json');
    const results = {
      element: {},
      editor: {}
    };

    if (window.Android) {
      flatterSupport(support.editor, 'editor');
      flatterSupport(support.element, 'element');

      this.getSupported = function () {
        appBridge.propagate('supported', 'get', JSON.stringify({
          editor: results.editor,
          element: results.element,
          fonts: INKIVE.C.FONT_LIST
        }));
      };
    } else {
      this.supported = JSON.stringify(_.extend(support, {
        fonts: INKIVE.C.FONT_LIST
      }));
    }

    function flatterSupport (data, result, path) {
      const isRecursive = _.isObject(data);

      if (!isRecursive) results[result][path] = data;
      else {
        _.each(data, function (val, key) {
          flatterSupport(val, result, path ? (path + '.' + key) : key);
        });
      }
    }
  }

  function initAnimationFacade () {
    this.animation = {
      call: (type, action, data) => {
        const eventName = type + (action ? action.charAt(0).toUpperCase() + action.slice(1) : '');
        const event = new CustomEvent(`Animation.${eventName}`, {
          detail: data || {}
        });
        document.dispatchEvent(event);
      }
    };
  }
};

AppBridge.prototype.propagate = function (sourceType, eventKey, data) {
  // INKIVE.C.IS_DEV && console.log('Bridge triggering: ' + sourceType + '.' + eventKey, data);
  const eventName = sourceType + '.' + eventKey;

  if (eventName === 'editor.page.saving.error') {
  // TODO: This is a temporary fix until IOS resolves the issue with the saving error message,
  // thread here: https://desygner-team.slack.com/archives/C021HKPTHNW/p1721779122476029?thread_ts=1721620285.617729&cid=C021HKPTHNW
    const iOSEventName = eventName === 'editor.page.saving.error' && !this.version.isGreaterThan('5.1.8')
      ? 'editor.page.403'
      : eventName;

    this.callIOS(iOSEventName, data);
    this.callAndroid(eventName, data);

    return;
  }

  this.callBoth(eventName, data);
};

AppBridge.prototype.callBoth = function (eventName, params) {
  // INKIVE.C.IS_DEV && console.log('AppBridge: call');
  if (!INKIVE.C.IS_MOBILE_APP) return false;

  window.Android && _callAndroid(eventName, params);
  window.Android || _callIOS(eventName, params);

  return true;
};

AppBridge.prototype.callIOS = function (eventName, params) {
  const isRightPlatform = INKIVE.C.IS_MOBILE_APP && !window.Android;
  isRightPlatform && _callIOS(eventName, params);

  return isRightPlatform;
};

AppBridge.prototype.callAndroid = function (eventName, params) {
  const isRightPlatform = INKIVE.C.IS_MOBILE_APP && window.Android;
  isRightPlatform && _callAndroid(eventName, params);

  return isRightPlatform;
};

var appBridge = new AppBridge();
module.exports = appBridge;

function _callAndroid (eventName, params) {
  let funcName = eventName.match(/(\w+)\.get\.svg_(\d+)/)
    ? eventName
    : humps.camelize(eventName);

  funcName = legacy.androidMethodName(funcName);
  params = legacy.androidParameters(_.extend(
    { cmd: funcName },
    { params }
  ));

  // INKIVE.C.IS_DEV && console.log('Calling Android ' + funcName + ', version ' + appBridge.version.get() + (
  //   appBridge.version.isLowerThan('1.0.3') ? ' (lower than 1.0.3)' : '(greater than 1.0.2)'
  // ));
  if (!appBridge.version.isApp('webrand') && !appBridge.version.isApp('pdf') && appBridge.version.isLowerThan('1.0.3')) {
    Android[funcName].apply(window.Android, params);
  } else {
    Android.trigger(JSON.stringify(params));
  }
}

function _callIOS (eventName, params) {
  let funcName = eventName.match(/(\w+)\.get\.svg_(\d+)/)
    ? eventName
    : humps.camelize(eventName);
  funcName = legacy.iosMethodName(funcName);

  // INKIVE.C.IS_DEV && console.log('Calling iOS ' + funcName);
  iOS.Trigger(legacy.iosParameters(
    _.extend(
      { cmd: funcName },
      { params }
    )
  ));
}

var iOS = {
  Trigger: function (object) {
    if (window.webkit && window.webkit.messageHandlers) {
      try {
        window.webkit.messageHandlers.main.postMessage(object);
      } catch (e) {
        INKIVE.C.DEBUG && console.warn(e);
      }
    } else {
      const $iframe = $('<iframe src="inkive://www.awesome.com/notyourbusiness.php?event=' + encodeURIComponent(JSON.stringify(object)) + '" width="0" height="0" border="0" style="display:none;" ></iframe>').appendTo('body');
      setTimeout(function () { $iframe.remove(); }, 1000);
    }
  }
};
